/*
 * ring() — a small radial gauge (ported from the NATS-fleet design's <Ring>), as a
 * template fn so it composes into cards/panels like base-ui's own primitives. Colors
 * come from the shared tokens (green/yellow/red). `value: null` renders the "pending"
 * state (track-only ring + "—") for metrics we don't have yet (CPU/MEM/DISK/TEMP).
 */
import { html, svg, type TemplateResult } from 'lit';

export interface RingOpts {
  /** null → pending (no telemetry yet). */
  value: number | null;
  max?: number;
  size?: number;
  stroke?: number;
  unit?: string;
  label: string;
  /** Colour semantics:
   *  'load' (default) — utilization/badness: green→yellow→red as it rises (high = bad).
   *    For cpu/mem/load/temp/disk/egress/error/etc.
   *  'activity' — a count/throughput where HIGH ISN'T BAD (flows, sessions, msg/s, conns):
   *    a STEADY blue arc that fills proportionally, never alarming red. */
  tone?: 'load' | 'activity';
}

export function ring(o: RingOpts): TemplateResult {
  const size = o.size ?? 46;
  const stroke = o.stroke ?? 4;
  const max = o.max ?? 100;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const has = o.value != null && Number.isFinite(o.value);
  const v = has ? Math.max(0, Math.min(max, o.value as number)) : 0;
  const pct = max > 0 ? v / max : 0;
  // Continuous green→yellow→red gradient — NO threshold cut-offs — reusing the
  // shared semantic tokens. color-mix interpolates in oklch so the colour sweep
  // stays perceptually even as the value oscillates; the arc itself animates via
  // the .fgc-ring-arc CSS transition (stroke + stroke-dashoffset).
  const color = !has
    ? 'var(--border-color)'
    : o.tone === 'activity'
      ? 'var(--blue)' // a count/throughput — steady, never alarming; arc still fills by magnitude
      : pct < 0.5
        ? `color-mix(in oklch, var(--green), var(--yellow) ${pct * 200}%)`
        : `color-mix(in oklch, var(--yellow), var(--red) ${(pct - 0.5) * 200}%)`;
  return html`
    <div class="fgc-ring">
      <div class="fgc-ring-dial" style="width:${size}px;height:${size}px">
        ${svg`<svg width=${size} height=${size} viewBox="0 0 ${size} ${size}">
          <g transform="rotate(-90 ${size / 2} ${size / 2})">
            <circle cx=${size / 2} cy=${size / 2} r=${r} fill="none"
              stroke="var(--bg-tertiary)" stroke-width=${stroke}></circle>
            ${
              has
                ? svg`<circle class="fgc-ring-arc" cx=${size / 2} cy=${size / 2} r=${r} fill="none"
                    stroke-width=${stroke} stroke-linecap="round" stroke-dasharray=${circ}
                    style=${`stroke:${color};stroke-dashoffset:${circ * (1 - pct)}`}></circle>`
                : null
            }
          </g>
        </svg>`}
        <div class="fgc-ring-val">
          ${has
            ? html`${Math.round(v)}<span class="fgc-ring-unit">${o.unit ?? '%'}</span>`
            : html`<span class="fgc-ring-na">—</span>`}
        </div>
      </div>
      <div class="fgc-ring-label">${o.label}</div>
    </div>
  `;
}
